import type { TDateString, TUuid } from './common';
import type { USER_POSITION_DEFAULT_FILTER } from '../utils/user';
import type { EUserPosition } from './user';

export enum EQualificationValidityType {
  UNLIMITED = 'UNLIMITED',
  /** Valid until Dec 31 of the record year. */
  ANNUAL = 'ANNUAL',
}

export enum EQualificationStatus {
  VALID = 'VALID',
  /** Recorded, but no record is valid any more: "abgelaufen – erneuern". */
  EXPIRED = 'EXPIRED',
  NOT_RECORDED = 'NOT_RECORDED',
}

export type TQualificationCategory = {
  id: TUuid
  name: string
};

export type TQualificationFacilityRelation = {
  facility_id: TUuid
};

/** Carries no `active` flag: the catalog response lists active and deactivated entries apart. */
export type TQualification = {
  id: TUuid
  name: string
  category: TQualificationCategory | null
  validity_type: EQualificationValidityType
  mandatory: boolean
  facilities: TQualificationFacilityRelation[]
};

/** One per physician, qualification and year; earlier years are kept. */
export type TQualificationRecord = {
  id: TUuid
  qualification: TUuid
  record_year: number
  /** Null means no end. Unless set by hand, an ANNUAL record ends on Dec 31 of its year. */
  valid_until: TDateString | null
  valid_until_is_manual: boolean
  comment: string | null
};

export type TQualificationRecordValidity = Pick<TQualificationRecord, 'record_year' | 'valid_until'>;

export type TQualificationEvaluation = Readonly<{
  status: EQualificationStatus
  validUntil: TDateString | null
}>;

export type TQualificationCounts = Readonly<{
  valid: number
  expired: number
  /** Every physician the qualification applies to among those the caller sees; the ratio's base. */
  total: number
}>;

export type TPhysicianPositionFilter = EUserPosition | typeof USER_POSITION_DEFAULT_FILTER;

/** Narrows the physicians a planner sees; every key left out matches everyone. */
export type TQualificationPhysicianFilter = Readonly<{
  /** Matched by `matchesNameSearch`. */
  search?: string
  /** The training goal or a completed Facharzt title. */
  targetSpeciality?: TUuid
  position?: TPhysicianPositionFilter
  /** 1 to MAX_TRAINING_YEAR; only rotating staff without a position have one. */
  trainingYear?: number
}>;

/** The whole tenant for a `main_facility_admin`, otherwise the planner's Stammklinik relations. */
export type TFacilityWriteScope =
  | { unrestricted: true }
  | { unrestricted: false, facilityIds: ReadonlySet<TUuid> };
